import React, { useCallback, useEffect, useRef } from "react"

import { createPortal } from "react-dom"

import useHasMounted from "hooks/useHasMounted"
import styles from "styles/modal.module.scss"
import { gsap } from "utils/gsap"

import FaIcon from "./FontAwesomeIcon"
import { Heading } from "./Text"

const Modal: React.FC<{
  getState: boolean
  setState: React.Dispatch<React.SetStateAction<boolean>>
  children: React.ReactNode
  title?: string | React.ReactNode
}> = ({ getState, setState, children, title }) => {
  const modalWrapper = useRef<HTMLDivElement>(null)
  const modalOverlay = useRef<HTMLDivElement>(null)
  const modalEl = useRef<HTMLDivElement>(null)
  const tl = useRef<GSAPTimeline | null>(null)

  const hasMounted = useHasMounted()
  const closeModal = useCallback(() => {
    tl.current?.reverse()
    setTimeout(() => {
      setState(false)
    }, 300)
  }, [setState])

  useEffect(() => {
    if (getState) {
      tl.current = gsap
        .timeline()
        .to(modalOverlay.current, {
          duration: 0.4,
          opacity: 0.75,
          ease: "power2.inOut"
        })
        .to(
          modalEl.current,
          {
            duration: 0.3,
            opacity: 1,
            scale: 1,

            ease: "power2.inOut"
          },
          "<"
        )

      // detect esc
      const handleEsc = (e: KeyboardEvent) => {
        if (e.key === "Escape") closeModal()
      }
      window.addEventListener("keydown", handleEsc)
      return () => window.removeEventListener("keydown", handleEsc)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getState])

  useEffect(() => {
    if (getState && hasMounted) {
      tl.current?.play()
    }
  }, [getState, hasMounted])

  if (!getState) return null

  return createPortal(
    <div className={styles.modalWrapper} ref={modalWrapper}>
      <div
        className={styles.modalOverlay}
        ref={modalOverlay}
        onClick={closeModal}
      />
      <div className={styles.modal} ref={modalEl}>
        <div className={styles.modalHeader}>
          <Heading>{title}</Heading>
          <FaIcon
            icon="times"
            size="2x"
            className={styles.closeIcon}
            onClick={closeModal}
          />
        </div>
        <div className={styles.modalContent}>{children}</div>
      </div>
    </div>,
    document.body
  )
}

export default Modal
